import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, autorun, runInAction} from '@api'
import {Button, FormGroup, InputGroup, Toast } from '@blueprintjs/core'
import { LinearProgress } from '@material-ui/core'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {CloudPDFEntry, IQueuedPdfServerJob} from '@modules/pdf'
import {aws, StoresInstance} from '@stores'
import { PrimaryIconButton } from 'centric-ui-components'
import * as css from './ToastPDFJob.css'

/// region Store

export class ToastPDFJobStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  updateProgressInterval : number

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {job: {status}} = this
        runInAction(() => {
          this.updateProgressInterval && window.clearTimeout(this.updateProgressInterval)
          switch (status) {
            case 'submitting': {
              this.progress = 10
              break
            }

            case 'accepted': {
              this.progress = 20
              break
            }

            case 'processing': {
              this.progress = 30
              this.updateProgressInterval = window.setInterval(() => {
                if (this.progress < 90) {
                  this.progress += 5
                }
                else {
                  window.clearTimeout(this.updateProgressInterval)
                  this.updateProgressInterval = null
                }
              }, 2000)
              break
            }

            case 'done': {
              this.progress = 100
              // setTimeout(() => {
              //   this.showProgress = false
              // }, 2000)
              break
            }
          }
        })
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @computed get showProgress() {
    return this.job.status !== 'pending'
  }

  @observable progress = 0

  @computed get statusLabel() {
    switch (this.job.status) {
      case 'pending': {
        return null
      }

      case 'submitting': {
        return 'Submitting'
        break
      }

      case 'accepted': {
        return 'Accepted'
        break
      }

      case 'processing': {
        return 'Generating'
      }

      case 'done': {
        return 'Finished'
      }
    }
  }

  get job() { return this.props.job}

  @action onDismissToast = () => {
    this.job.hideToast = true
    this.cloudPDF.removeJob(this.job)
  }

  @computed get nestPdfHref() {
    const {job: {lastStatusUpdate}, stores: {nestJSUrl}} = this
    return lastStatusUpdate?.filename ? `${nestJSUrl}/pdf/${lastStatusUpdate.filename}` : null
  }

  @computed get pdfHref() {
    const {job: {lastStatusUpdate}} = this
    return lastStatusUpdate?.s3Key ? aws.urlFor(lastStatusUpdate.s3Key) : null
  }

  @action openPdf = () => {
    const {flexLayout: {tabsByComponent, create: {createPdfTab}}, job: {lastStatusUpdate}} = this,
      existingTab = _.first(tabsByComponent.pdfViewer)
    const tab = createPdfTab({ config: {pdfSettings: {selectedArtifact: lastStatusUpdate.filename}}, tab: existingTab})
    !existingTab && tab.move('right')
    tab.select()
    !DEBUG && this.onDismissToast()
  }

  @action onChangeFilename = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.job.pdfInput.filename = e.currentTarget.value
  }

  @action onCancelJob = () => {
    const {job, stores: {currentUser}, cloudPDF: {pendingJobs}} = this
    currentUser.pendingPdfJobs.remove(job)
  }

  @action onSubmitJob = async () => {
    const {job, stores: {cloudPDF}} = this
    await cloudPDF.submitPdfJob(job)
  }
}

export const ToastPDFJobStoreContext = React.createContext<ToastPDFJobStore>(null)
export const useToastPDFJobStore = () => React.useContext(ToastPDFJobStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  job: IQueuedPdfServerJob
}

export const ToastPDFJob = observer<MyProps>(function ToastPDFJob({className, ...props}) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new ToastPDFJobStore(stores, obsProps)),
    {onDismissToast, onChangeFilename, job, showProgress, progress, statusLabel, nestPdfHref, pdfHref, openPdf, onCancelJob, onSubmitJob} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <ToastPDFJobStoreContext.Provider value={store}>
      <Toast className={classNames(css.root, className)}
             timeout={0}
             onDismiss={onDismissToast}
             message={<div className={css.message}>
               <div className={css.header}>
                 <span className={css.title}>{job.status === 'pending' ? 'Create PDF' : job.status === 'done' ? 'PDF Generated' :  'Generating PDF...'}</span>
                 <PrimaryIconButton icon='cross' tooltip='Hide' tooltipStyle='mui' wrapperClassName={css.close} onClick={onDismissToast}  />
               </div>

               <div className={css.optionsContainer}>
                 <FormGroup
                   label='Filename:'
                   labelFor='filename'
                   //inline={job.status !== 'pending'}
                   >
                   {job.status === 'pending' ? <InputGroup id='filename' onChange={onChangeFilename} placeholder={job.defaultFilename} value={job.pdfInput.filename} />
                                             : <span>{job.pdfInput?.filename || job.defaultFilename}</span>}
                 </FormGroup>
               </div>

               {false && job.status !== 'pending' && <div className={css.statusContainer}>
                 {job.lastStatusUpdate ? <a href={pdfHref} target='_blank' className={css.filename}>{job.lastStatusUpdate.filename}</a>
                                       : <span className={css.filename}>Starting PDF Generation...</span>}
               </div>}
               {showProgress && <div className={css.progressContainer}>
                <LinearProgress className={css.progress} classes={{ bar: css.progressBar}} value={progress} variant='determinate'/>
                   <label>{progress}%</label>
                 </div>}

               {job.status === 'pending' &&
                <div className={css.pendingActions}>
                    <Button text='Cancel' intent='none' onClick={onCancelJob} />
	                <Button text='Submit' intent='primary' onClick={onSubmitJob} />
               </div>}

               {job.status === 'done' && job.lastStatusUpdate &&
                <div className={css.pdfReadyActions}>
                  {DEBUG && <a download={job.lastStatusUpdate?.filename} href={nestPdfHref}>Download</a>}
                  <a href={pdfHref} target='_blank'>Open In Browser</a>
	                <a onClick={openPdf}>Open Here</a>
                </div>}
             </div>}
      />
    </ToastPDFJobStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/pdf/serverRenderedPDF/components/ToastPDFJob.tsx